import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  TeamIdentitySection,
  AppearanceSection,
  NavigationSection,
  MatchDefaultsSection,
  MatchStatsSection,
  MatchDayModeSection,
  DrillPresetsSection,
  OpponentsCompetitionsSection,
  SecuritySection,
  PreviewSection,
} from '../components/settings';
import { DEFAULT_MENU_COLOR, DEFAULT_BUTTON_COLOR, DEFAULT_PAGE_ORDER, mergePageOrder } from '../components/settings/constants';
import { ArrowLeft, Save, Loader2, RotateCcw, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_PRESETS = ['Injured', 'Suspended', 'Dropped', 'Holiday', 'Work', 'Unavailable'];

// Whitelist of fields that the season can override. Mirrors the
// SEASON_OVERRIDABLE_FIELDS set on the backend.
const OVERRIDABLE = [
  'team_name', 'club_badge', 'header_image', 'dressing_room_header_image',
  'ticker_enabled', 'ticker_mode', 'ticker_label', 'ticker_text',
  'menu_color', 'button_color',
  'sidebar_bg_image', 'sidebar_bg_opacity', 'sidebar_bg_offset',
  'preferred_formation', 'default_kick_off_time', 'half_length',
  'extra_time_half_length', 'preset_non_selection_reasons',
  'match_day_mode_enabled', 'voice_button_position', 'voice_control_enabled',
  'show_opponent_badges', 'countdown_timer_enabled', 'coach_note_categories',
  'timeline_auto_stats',
  'security_emails_enabled',
  'hidden_pages', 'page_order',
  'drill_presets',
];

// Account defaults read from the user record (with sensible fallbacks).
function readAccountDefaults(user) {
  return {
    team_name: user?.team_name || '',
    club_badge: user?.club_badge || '',
    header_image: user?.header_image || '',
    dressing_room_header_image: user?.dressing_room_header_image || '',
    ticker_enabled: user?.ticker_enabled || false,
    ticker_mode: user?.ticker_mode || 'manual',
    ticker_label: user?.ticker_label || 'BREAKING NEWS:',
    ticker_text: user?.ticker_text || '',
    menu_color: user?.menu_color || DEFAULT_MENU_COLOR,
    button_color: user?.button_color || DEFAULT_BUTTON_COLOR,
    sidebar_bg_image: user?.sidebar_bg_image || '',
    sidebar_bg_opacity: user?.sidebar_bg_opacity ?? 0.3,
    sidebar_bg_offset: user?.sidebar_bg_offset ?? 0,
    preferred_formation: user?.preferred_formation || '',
    default_kick_off_time: user?.default_kick_off_time || '',
    half_length: user?.half_length ?? 45,
    extra_time_half_length: user?.extra_time_half_length ?? 15,
    preset_non_selection_reasons: user?.preset_non_selection_reasons || DEFAULT_PRESETS,
    match_day_mode_enabled: user?.match_day_mode_enabled !== false,
    voice_button_position: user?.voice_button_position || 'right',
    voice_control_enabled: user?.voice_control_enabled !== false,
    show_opponent_badges: user?.show_opponent_badges !== false,
    countdown_timer_enabled: user?.countdown_timer_enabled !== false,
    coach_note_categories: user?.coach_note_categories || ['Shape', 'Defence', 'Attack', 'Set Pieces', 'Formation'],
    timeline_auto_stats: user?.timeline_auto_stats || false,
    security_emails_enabled: user?.security_emails_enabled !== false,
    hidden_pages: user?.hidden_pages || [],
    page_order: mergePageOrder(user?.page_order, DEFAULT_PAGE_ORDER),
    drill_presets: user?.drill_presets || [],
  };
}

// Deep-equality good enough for our shapes (primitives + arrays of primitives + simple objects).
function isEqual(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => isEqual(v, b[i]));
  }
  if (typeof a === 'object' && a && typeof b === 'object' && b) {
    const ak = Object.keys(a); const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    return ak.every((k) => isEqual(a[k], b[k]));
  }
  return false;
}

export default function EditSeason() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();

  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const accountDefaults = useMemo(() => readAccountDefaults(user), [user]);

  // Form state — initialised once season loads. Fields default to account
  // values; on save, only fields that differ from account defaults are
  // persisted as season overrides (the "inherit unless overridden" model).
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [teamName, setTeamName] = useState(accountDefaults.team_name);
  const [clubBadge, setClubBadge] = useState(accountDefaults.club_badge);
  const [badgePreview, setBadgePreview] = useState(accountDefaults.club_badge);
  const [badgeFile, setBadgeFile] = useState(null);
  const [headerImage, setHeaderImage] = useState(accountDefaults.header_image);
  const [headerPreview, setHeaderPreview] = useState(accountDefaults.header_image);
  const [headerFile, setHeaderFile] = useState(null);
  const [dressingRoomHeader, setDressingRoomHeader] = useState(accountDefaults.dressing_room_header_image);
  const [dressingRoomHeaderPreview, setDressingRoomHeaderPreview] = useState(accountDefaults.dressing_room_header_image);
  const [dressingRoomHeaderFile, setDressingRoomHeaderFile] = useState(null);

  const [tickerEnabled, setTickerEnabled] = useState(accountDefaults.ticker_enabled);
  const [tickerMode, setTickerMode] = useState(accountDefaults.ticker_mode);
  const [tickerLabel, setTickerLabel] = useState(accountDefaults.ticker_label);
  const [tickerText, setTickerText] = useState(accountDefaults.ticker_text);

  const [menuColor, setMenuColor] = useState(accountDefaults.menu_color);
  const [buttonColor, setButtonColor] = useState(accountDefaults.button_color);
  const [sidebarBgImage, setSidebarBgImage] = useState(accountDefaults.sidebar_bg_image);
  const [sidebarBgPreview, setSidebarBgPreview] = useState(accountDefaults.sidebar_bg_image);
  const [sidebarBgFile, setSidebarBgFile] = useState(null);
  const [sidebarBgOpacity, setSidebarBgOpacity] = useState(accountDefaults.sidebar_bg_opacity);
  const [sidebarBgOffset, setSidebarBgOffset] = useState(accountDefaults.sidebar_bg_offset);

  const [hiddenPages, setHiddenPages] = useState(accountDefaults.hidden_pages);
  const [pageOrder, setPageOrder] = useState(accountDefaults.page_order);
  const [draggedPage, setDraggedPage] = useState(null);
  const [dragOverPage, setDragOverPage] = useState(null);

  const [preferredFormation, setPreferredFormation] = useState(accountDefaults.preferred_formation);
  const [defaultKickOffTime, setDefaultKickOffTime] = useState(accountDefaults.default_kick_off_time);
  const [halfLength, setHalfLength] = useState(accountDefaults.half_length);
  const [extraTimeHalfLength, setExtraTimeHalfLength] = useState(accountDefaults.extra_time_half_length);
  const [presetReasons, setPresetReasons] = useState(accountDefaults.preset_non_selection_reasons);
  const [countdownTimerEnabled, setCountdownTimerEnabled] = useState(accountDefaults.countdown_timer_enabled);
  const [noteCategories, setNoteCategories] = useState(accountDefaults.coach_note_categories);

  const [timelineAutoStats, setTimelineAutoStats] = useState(accountDefaults.timeline_auto_stats);

  const [matchDayModeEnabled, setMatchDayModeEnabled] = useState(accountDefaults.match_day_mode_enabled);
  const [voiceButtonPosition, setVoiceButtonPosition] = useState(accountDefaults.voice_button_position);
  const [voiceControlEnabled, setVoiceControlEnabled] = useState(accountDefaults.voice_control_enabled);

  const [showOpponentBadges, setShowOpponentBadges] = useState(accountDefaults.show_opponent_badges);
  const [drillPresets, setDrillPresets] = useState(accountDefaults.drill_presets);
  const [securityEmailsEnabled, setSecurityEmailsEnabled] = useState(accountDefaults.security_emails_enabled);

  const togglePageVisibility = (pageId) => {
    setHiddenPages((prev) => (prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId]));
  };

  const initialisedRef = useRef(false);
  useEffect(() => {
    if (!seasonId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await axios.get(`${API}/seasons/${seasonId}/full`);
        if (cancelled) return;
        const s = resp.data;
        setSeason(s);
        setName(s.name || '');
        setStartDate(s.start_date || '');
        setEndDate(s.end_date || '');
        // Apply season overrides on top of account defaults
        const overlay = (key, val) => (s.settings && s.settings[key] !== undefined && s.settings[key] !== null ? s.settings[key] : val);
        setTeamName(overlay('team_name', accountDefaults.team_name));
        setClubBadge(overlay('club_badge', accountDefaults.club_badge));
        setBadgePreview(overlay('club_badge', accountDefaults.club_badge));
        setHeaderImage(overlay('header_image', accountDefaults.header_image));
        setHeaderPreview(overlay('header_image', accountDefaults.header_image));
        setDressingRoomHeader(overlay('dressing_room_header_image', accountDefaults.dressing_room_header_image));
        setDressingRoomHeaderPreview(overlay('dressing_room_header_image', accountDefaults.dressing_room_header_image));
        setTickerEnabled(overlay('ticker_enabled', accountDefaults.ticker_enabled));
        setTickerMode(overlay('ticker_mode', accountDefaults.ticker_mode));
        setTickerLabel(overlay('ticker_label', accountDefaults.ticker_label));
        setTickerText(overlay('ticker_text', accountDefaults.ticker_text));
        setMenuColor(overlay('menu_color', accountDefaults.menu_color));
        setButtonColor(overlay('button_color', accountDefaults.button_color));
        setSidebarBgImage(overlay('sidebar_bg_image', accountDefaults.sidebar_bg_image));
        setSidebarBgPreview(overlay('sidebar_bg_image', accountDefaults.sidebar_bg_image));
        setSidebarBgOpacity(overlay('sidebar_bg_opacity', accountDefaults.sidebar_bg_opacity));
        setSidebarBgOffset(overlay('sidebar_bg_offset', accountDefaults.sidebar_bg_offset));
        setHiddenPages(overlay('hidden_pages', accountDefaults.hidden_pages));
        setPageOrder(overlay('page_order', accountDefaults.page_order));
        setPreferredFormation(overlay('preferred_formation', accountDefaults.preferred_formation));
        setDefaultKickOffTime(overlay('default_kick_off_time', accountDefaults.default_kick_off_time));
        setHalfLength(overlay('half_length', accountDefaults.half_length));
        setExtraTimeHalfLength(overlay('extra_time_half_length', accountDefaults.extra_time_half_length));
        setPresetReasons(overlay('preset_non_selection_reasons', accountDefaults.preset_non_selection_reasons));
        setCountdownTimerEnabled(overlay('countdown_timer_enabled', accountDefaults.countdown_timer_enabled));
        setNoteCategories(overlay('coach_note_categories', accountDefaults.coach_note_categories));
        setTimelineAutoStats(overlay('timeline_auto_stats', accountDefaults.timeline_auto_stats));
        setMatchDayModeEnabled(overlay('match_day_mode_enabled', accountDefaults.match_day_mode_enabled));
        setVoiceButtonPosition(overlay('voice_button_position', accountDefaults.voice_button_position));
        setVoiceControlEnabled(overlay('voice_control_enabled', accountDefaults.voice_control_enabled));
        setShowOpponentBadges(overlay('show_opponent_badges', accountDefaults.show_opponent_badges));
        setDrillPresets(overlay('drill_presets', accountDefaults.drill_presets));
        setSecurityEmailsEnabled(overlay('security_emails_enabled', accountDefaults.security_emails_enabled));
        initialisedRef.current = true;
      } catch (e) {
        toast.error('Failed to load season');
        navigate('/seasons');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId]);

  // Compute the override count to show "X overrides applied" badge
  const currentValues = {
    team_name: teamName, club_badge: clubBadge, header_image: headerImage,
    dressing_room_header_image: dressingRoomHeader,
    ticker_enabled: tickerEnabled, ticker_mode: tickerMode, ticker_label: tickerLabel, ticker_text: tickerText,
    menu_color: menuColor, button_color: buttonColor,
    sidebar_bg_image: sidebarBgImage, sidebar_bg_opacity: sidebarBgOpacity, sidebar_bg_offset: sidebarBgOffset,
    preferred_formation: preferredFormation, default_kick_off_time: defaultKickOffTime,
    half_length: halfLength, extra_time_half_length: extraTimeHalfLength,
    preset_non_selection_reasons: presetReasons,
    match_day_mode_enabled: matchDayModeEnabled, voice_button_position: voiceButtonPosition, voice_control_enabled: voiceControlEnabled,
    show_opponent_badges: showOpponentBadges, countdown_timer_enabled: countdownTimerEnabled, coach_note_categories: noteCategories,
    timeline_auto_stats: timelineAutoStats,
    security_emails_enabled: securityEmailsEnabled,
    hidden_pages: hiddenPages, page_order: pageOrder,
    drill_presets: drillPresets,
  };
  const overrideCount = OVERRIDABLE.reduce((acc, k) => (isEqual(currentValues[k], accountDefaults[k]) ? acc : acc + 1), 0);

  const isActive = user?.current_season_id === seasonId;

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const resp = await axios.post(`${API}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return resp.data.image_url;
  };

  const resetAllOverrides = () => {
    if (!window.confirm('Reset every field to account default? This will clear all season-specific overrides.')) return;
    setTeamName(accountDefaults.team_name); setClubBadge(accountDefaults.club_badge); setBadgePreview(accountDefaults.club_badge);
    setHeaderImage(accountDefaults.header_image); setHeaderPreview(accountDefaults.header_image);
    setDressingRoomHeader(accountDefaults.dressing_room_header_image); setDressingRoomHeaderPreview(accountDefaults.dressing_room_header_image);
    setTickerEnabled(accountDefaults.ticker_enabled); setTickerMode(accountDefaults.ticker_mode);
    setTickerLabel(accountDefaults.ticker_label); setTickerText(accountDefaults.ticker_text);
    setMenuColor(accountDefaults.menu_color); setButtonColor(accountDefaults.button_color);
    setSidebarBgImage(accountDefaults.sidebar_bg_image); setSidebarBgPreview(accountDefaults.sidebar_bg_image);
    setSidebarBgOpacity(accountDefaults.sidebar_bg_opacity); setSidebarBgOffset(accountDefaults.sidebar_bg_offset);
    setHiddenPages(accountDefaults.hidden_pages); setPageOrder(accountDefaults.page_order);
    setPreferredFormation(accountDefaults.preferred_formation); setDefaultKickOffTime(accountDefaults.default_kick_off_time);
    setHalfLength(accountDefaults.half_length); setExtraTimeHalfLength(accountDefaults.extra_time_half_length);
    setPresetReasons(accountDefaults.preset_non_selection_reasons);
    setCountdownTimerEnabled(accountDefaults.countdown_timer_enabled); setNoteCategories(accountDefaults.coach_note_categories);
    setTimelineAutoStats(accountDefaults.timeline_auto_stats);
    setMatchDayModeEnabled(accountDefaults.match_day_mode_enabled);
    setVoiceButtonPosition(accountDefaults.voice_button_position); setVoiceControlEnabled(accountDefaults.voice_control_enabled);
    setShowOpponentBadges(accountDefaults.show_opponent_badges); setDrillPresets(accountDefaults.drill_presets);
    setSecurityEmailsEnabled(accountDefaults.security_emails_enabled);
    toast.info('All fields reset to account defaults — click Save to persist');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Upload any pending image files first
      let finalClubBadge = clubBadge, finalHeaderImage = headerImage;
      let finalDressingRoomHeader = dressingRoomHeader, finalSidebarBgImage = sidebarBgImage;
      if (badgeFile) { finalClubBadge = await uploadFile(badgeFile); setClubBadge(finalClubBadge); setBadgeFile(null); }
      if (headerFile) { finalHeaderImage = await uploadFile(headerFile); setHeaderImage(finalHeaderImage); setHeaderFile(null); }
      if (dressingRoomHeaderFile) { finalDressingRoomHeader = await uploadFile(dressingRoomHeaderFile); setDressingRoomHeader(finalDressingRoomHeader); setDressingRoomHeaderFile(null); }
      if (sidebarBgFile) { finalSidebarBgImage = await uploadFile(sidebarBgFile); setSidebarBgImage(finalSidebarBgImage); setSidebarBgPreview(finalSidebarBgImage); setSidebarBgFile(null); }

      // Update season name/dates
      await axios.put(`${API}/seasons/${seasonId}`, {
        name: name || season?.name,
        start_date: startDate || season?.start_date,
        end_date: endDate || null,
      });

      // Build the override dict — only include fields that differ from account defaults
      const finalValues = { ...currentValues, club_badge: finalClubBadge, header_image: finalHeaderImage, dressing_room_header_image: finalDressingRoomHeader, sidebar_bg_image: finalSidebarBgImage };
      const overrides = {};
      OVERRIDABLE.forEach((k) => {
        if (!isEqual(finalValues[k], accountDefaults[k])) {
          overrides[k] = finalValues[k];
        }
      });

      await axios.put(`${API}/seasons/${seasonId}/settings`, overrides);

      toast.success(`Season saved — ${Object.keys(overrides).length} override${Object.keys(overrides).length === 1 ? '' : 's'} active`);
      // If this is the active season, refresh the user record so the rest of
      // the app picks up the new values immediately.
      if (isActive) await fetchUser();
    } catch (e) {
      toast.error('Failed to save season settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 page-slide-in max-w-3xl" data-testid="edit-season-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/seasons')} className="rounded-xl" data-testid="back-to-seasons-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 font-title tracking-tight">
                {season?.name || 'Edit Season'}
              </h1>
              {isActive && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" data-testid="active-season-badge">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Currently active
                </Badge>
              )}
            </div>
            <p className="text-gray-500 mt-1 text-sm">
              Settings here override your account defaults for this season only.
              {' '}<span className="text-gray-700 font-medium">{overrideCount}</span> override{overrideCount === 1 ? '' : 's'} active.
            </p>
          </div>
        </div>

        {/* Season name & dates */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg">Season Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Season Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl" data-testid="season-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl" data-testid="season-start-date-input" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-xl" data-testid="season-end-date-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex items-start gap-3">
          <RotateCcw className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            Each field below starts at your account default. Change a field to create a season-specific override.
            Set it back to the account default value to remove the override and inherit again.
          </div>
          <Button variant="ghost" size="sm" onClick={resetAllOverrides} className="rounded-lg shrink-0 text-amber-900 hover:bg-amber-100" data-testid="reset-all-overrides-btn">
            Reset all
          </Button>
        </div>

        <TeamIdentitySection
          teamName={teamName}
          setTeamName={setTeamName}
          badgePreview={badgePreview}
          setBadgePreview={setBadgePreview}
          badgeFile={badgeFile}
          setBadgeFile={setBadgeFile}
          headerPreview={headerPreview}
          setHeaderPreview={setHeaderPreview}
          headerFile={headerFile}
          setHeaderFile={setHeaderFile}
          dressingRoomHeaderPreview={dressingRoomHeaderPreview}
          setDressingRoomHeaderPreview={setDressingRoomHeaderPreview}
          dressingRoomHeaderFile={dressingRoomHeaderFile}
          setDressingRoomHeaderFile={setDressingRoomHeaderFile}
          setDressingRoomHeader={setDressingRoomHeader}
          tickerEnabled={tickerEnabled}
          setTickerEnabled={setTickerEnabled}
          tickerMode={tickerMode}
          setTickerMode={setTickerMode}
          tickerLabel={tickerLabel}
          setTickerLabel={setTickerLabel}
          tickerText={tickerText}
          setTickerText={setTickerText}
          setClubBadge={setClubBadge}
          setHeaderImage={setHeaderImage}
        />

        <AppearanceSection
          menuColor={menuColor}
          setMenuColor={setMenuColor}
          buttonColor={buttonColor}
          setButtonColor={setButtonColor}
          sidebarBgImage={sidebarBgImage}
          sidebarBgOpacity={sidebarBgOpacity}
          setSidebarBgOpacity={setSidebarBgOpacity}
          sidebarBgFile={sidebarBgFile}
          setSidebarBgFile={setSidebarBgFile}
          sidebarBgPreview={sidebarBgPreview}
          setSidebarBgPreview={setSidebarBgPreview}
          setSidebarBgImage={setSidebarBgImage}
          sidebarBgOffset={sidebarBgOffset}
          setSidebarBgOffset={setSidebarBgOffset}
        />

        <NavigationSection
          pageOrder={pageOrder}
          setPageOrder={setPageOrder}
          hiddenPages={hiddenPages}
          togglePageVisibility={togglePageVisibility}
          draggedPage={draggedPage}
          setDraggedPage={setDraggedPage}
          dragOverPage={dragOverPage}
          setDragOverPage={setDragOverPage}
        />

        <SecuritySection
          securityEmailsEnabled={securityEmailsEnabled}
          setSecurityEmailsEnabled={setSecurityEmailsEnabled}
        />

        <MatchDefaultsSection
          preferredFormation={preferredFormation}
          setPreferredFormation={setPreferredFormation}
          defaultKickOffTime={defaultKickOffTime}
          setDefaultKickOffTime={setDefaultKickOffTime}
          halfLength={halfLength}
          setHalfLength={setHalfLength}
          extraTimeHalfLength={extraTimeHalfLength}
          setExtraTimeHalfLength={setExtraTimeHalfLength}
          presetReasons={presetReasons}
          setPresetReasons={setPresetReasons}
          countdownTimerEnabled={countdownTimerEnabled}
          setCountdownTimerEnabled={setCountdownTimerEnabled}
          noteCategories={noteCategories}
          setNoteCategories={setNoteCategories}
        />

        <MatchStatsSection
          timelineAutoStats={timelineAutoStats}
          setTimelineAutoStats={setTimelineAutoStats}
        />

        <MatchDayModeSection
          matchDayModeEnabled={matchDayModeEnabled}
          setMatchDayModeEnabled={setMatchDayModeEnabled}
          voiceButtonPosition={voiceButtonPosition}
          setVoiceButtonPosition={setVoiceButtonPosition}
          voiceControlEnabled={voiceControlEnabled}
          setVoiceControlEnabled={setVoiceControlEnabled}
        />

        <DrillPresetsSection
          drillPresets={drillPresets}
          setDrillPresets={setDrillPresets}
        />

        <OpponentsCompetitionsSection
          showOpponentBadges={showOpponentBadges}
          setShowOpponentBadges={setShowOpponentBadges}
        />

        <PreviewSection
          menuColor={menuColor}
          badgePreview={badgePreview}
          teamName={teamName}
          setBadgePreview={setBadgePreview}
        />

        <div className="sticky bottom-4 z-30 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="rounded-xl shadow-lg bg-[#064E3B] hover:bg-[#065f46] text-white"
            data-testid="save-season-settings-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save Season Settings</>}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
